import { CanceledError } from 'axios';
import each from 'lodash/each';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import { defineStore } from 'pinia';

import { useCompaniesStore } from '@/features/Companies/store';
import { useContract } from '@/features/Contract/useContract/useContract';
import { useFeaturesStore } from '@/features/Features/store';
import { useGlobalStore } from '@/features/Global/store';
import { transferVorgabenDataToStores } from '@/features/Vorgaben/utils';
import type { SearchData } from '@/features/Global/types';
import type { SortSelection } from '@/features/Tariffs/sorting';
import {
  createDefaultSortingState,
  sortables,
} from '@/features/Tariffs/sorting';
import {
  applyTranslation,
  filterActiveTariffs,
  filterTariffs,
} from '@/features/Tariffs/tariffStoreUtilities';
import type {
  BaseTariff,
  TariffChunk,
  TariffError,
  TariffFilters,
  TariffNoteMap,
  TariffResponseErrorNote,
  TariffResponseNote,
  TariffSearchRequestBody,
  TariffWithDocuments,
} from '@/features/Tariffs/types';
import {
  TARIFF_SEARCH_REQUEST_IDENTIFIER,
  TariffErrorNoteMap,
} from '@/features/Tariffs/types';
import BackendValidationError from '@/features/validation/backendValidation';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import splitArray from '@/utils/splitArray/splitArray';
import type { UseLogger } from '@/services/logger/useLogger';
import { useBrokerStore } from '@/features/Broker/store';
import { EntityResourceContainer } from '@/features/EntityManager/types';
import { useInputStore } from '@/features/Sparten';
import {
  SIMPLE_CHOICE,
  SimpleChoiceOptionGesellschaftenTs,
} from '@/features/ChoicesLists/Simple/types';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import { tariffErrorOverride } from '@/features/Tariffs/tariffErrorOverride';

export interface TariffStoreState {
  tariffChunks: Record<string, TariffChunk>;
  allTimeTariffs: BaseTariff[];

  selection: string[];
  recommendation: string | null;

  documents: Record<string, { name: string; checked: boolean }[]>;

  requests: {
    maxCount: number;
    runningCount: number;
    lastHashCode: string;
    cancelFunctions: CallableFunction[];
  };

  filters: TariffFilters;

  localFilters: {
    search: string[];
  };

  links: {
    log: string;
  };

  calculationId: string;

  sorting: SortSelection[];

  notes: TariffResponseNote[]; // Non-unique list of all notes -> use the uniqueNotes getter for accessing

  errorNotes: TariffErrorNoteMap;
}

export const useTariffStore = defineStore('TariffStore', {
  state: (): TariffStoreState => {
    const config = getCurrentSpartenConfig();

    return {
      tariffChunks: {},
      allTimeTariffs: [],

      selection: [],
      recommendation: null,

      documents: {},

      requests: {
        maxCount: config.maxSimultaneousCount,
        runningCount: 0,
        lastHashCode: '',
        cancelFunctions: [],
      },

      filters: {
        directAgreement: false,
      },

      localFilters: {
        search: [],
      },

      links: {
        log: '',
      },

      calculationId: '',

      sorting: createDefaultSortingState(),

      notes: [],

      errorNotes: {},
    };
  },

  actions: {
    async loadTariffs(append = false, logger?: UseLogger) {
      const searchData = useGlobalStore().getTariffSearchData(true);

      const companies = useCompaniesStore().companiesSelection;
      const companyIds: string[] = [];
      const isDirectAgreement = this.filters.directAgreement;

      const currentHashCode = useGlobalStore().getTariffSearchHash();

      if (currentHashCode === this.requests.lastHashCode) {
        if (this.allTariffs.length !== 0) {
          logger?.info(
            'stop loading tariffs due to same hash',
            currentHashCode,
          );
          return;
        }

        logger?.info(
          'allTariffs is empty - continue loading even hash matches',
        );
      }

      // reset possible contract errors from previous requests
      useContract().resetErrorState();

      // after we're sure we make new requests clear tariffs if not appending
      if (!append) {
        this.tariffChunks = {};
        this.allTimeTariffs = [];
      }

      const config = getCurrentSpartenConfig();

      for (let i = 0; i < companies.length; i += 1) {
        // when filter directAgreement is false just allow all companies
        // otherwise test if company has a direct agreement
        if (!isDirectAgreement || companies[i].isDirektvereinbarung) {
          if (this.tariffChunks[companies[i].insuranceId] === undefined) {
            companyIds.push(companies[i].insuranceId);
          }
        }
      }

      if (companyIds.length === 0) {
        // Alle Gesellschaften des Filters liegen bereits vor, die Suche ist damit vollständig
        // beantwortet. Der Hash muss trotzdem festgehalten werden: Sonst gilt der nächste
        // Aufruf ohne append (Vorgabenseite → Tarifliste, „Zurück" aus dem Antrag) als neue
        // Suche und rechnet alles neu, obwohl sich nur der Filter geändert hat (VGL-7196).
        this.requests.lastHashCode = currentHashCode;
        logger?.info('stop loading tariffs due to missing companies');
        return;
      }

      if (this.requests.runningCount > 0) {
        this.requests.cancelFunctions.forEach((cancel) => cancel() as void);
        this.requests.cancelFunctions = [];
      }

      let requestCount = config.maxSimultaneousCount;

      if (requestCount !== -1) {
        requestCount =
          companies.length <
          config.maxSimultaneousCount * config.maxSimultaneousCount
            ? 1
            : config.maxSimultaneousCount;
      } else {
        requestCount = 1;
      }

      this.requests.maxCount = requestCount;

      const { getAlternateTariffSearchRequestIdentifier } =
        getCurrentSpartenConfig();

      const alternateTariffSearchRequestIdentifier =
        getAlternateTariffSearchRequestIdentifier !== undefined
          ? getAlternateTariffSearchRequestIdentifier()
          : undefined;

      const requests: Promise<void>[] = [];
      let relevantCompanyIds = companyIds;

      if (
        alternateTariffSearchRequestIdentifier ===
        TARIFF_SEARCH_REQUEST_IDENTIFIER.KFZ_AENDERUNGSANTRAG
      ) {
        const selection = useSimpleChoicesStore().data[
          SIMPLE_CHOICE.GESELLSCHAFTEN_TS
        ].values.find(
          (company) =>
            company.value === useInputStore().vorversicherung.gesellschaftID,
        ) as SimpleChoiceOptionGesellschaftenTs | undefined;

        if (selection === undefined) {
          throw new VglError(VGL_ERROR_CODE.TARIFFS_LOADING_SPECIAL_FAILED, {
            additionalErrorMessage: `missing gesellschaften_ts id (id: ${useInputStore().vorversicherung.gesellschaftID}) to map Änderungsantrag`,
          });
        }

        const specialRequestCompanyId = companyIds.filter(
          (id) => Number(id) === selection.insuranceId,
        );

        requests.push(
          this.loadChunk(
            searchData,
            specialRequestCompanyId,
            alternateTariffSearchRequestIdentifier,
            logger,
          ),
        );

        logger?.info(
          `start loading special tariffs with alternateTariffSearchRequestIdentifier: ${alternateTariffSearchRequestIdentifier} companies: ${JSON.stringify(specialRequestCompanyId)}`,
        );

        relevantCompanyIds = companyIds.filter(
          (id) => Number(id) !== selection.insuranceId,
        );
      }

      const chunkedCompanyIds = splitArray(
        relevantCompanyIds,
        requestCount,
        false,
      );

      logger?.info(
        `start loading tariffs with max requestCount of ${this.requests.maxCount}`,
      );

      chunkedCompanyIds.map((chunk, index) => {
        let chunkTariffSearchRequestIdentifier;
        if (
          alternateTariffSearchRequestIdentifier ===
            TARIFF_SEARCH_REQUEST_IDENTIFIER.ALL_TIME_TARIFF &&
          index === 0
        ) {
          chunkTariffSearchRequestIdentifier =
            alternateTariffSearchRequestIdentifier;
        }

        return requests.push(
          this.loadChunk(
            searchData,
            chunk,
            chunkTariffSearchRequestIdentifier,
            logger,
          ),
        );
      });

      useFeaturesStore().clearCachedTariffFeatures();
      this.errorNotes = {};
      this.notes = [];

      this.requests.lastHashCode = currentHashCode;

      await Promise.all(requests).catch((error) => {
        if (error instanceof BackendValidationError) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.TARIFFS_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)} - Append ${
            append ? 'yes' : 'no'
          }`,
          cause: error,
        });
      });

      this.verifySelectionAndRecommendation();
      this.requests.cancelFunctions = [];
    },

    async loadChunk(
      searchData: SearchData<EntityResourceContainer>,
      companyIdsChunk: string[],
      alternateTariffSearchRequestIdentifier?: TARIFF_SEARCH_REQUEST_IDENTIFIER,
      logger?: UseLogger,
    ) {
      const { kfzVersion } = getCurrentSpartenConfig();

      const requestBody: TariffSearchRequestBody = {
        ...searchData,
        konfigurationsParameter: {
          direktvereinbarung: this.filters.directAgreement,
          gesellschaftsfilter: companyIdsChunk.map((id) =>
            // @todo needs to be removed when backend accepts string values here. Also adjust type!
            Number.parseInt(id, 10),
          ),
          alternateTariffSearchRequestIdentifier,
          ...(kfzVersion !== undefined ? { kfzVersion } : {}),
        },
      };

      // create data containers for requested companies
      companyIdsChunk.forEach((id) => {
        this.tariffChunks[id] = {
          tariffErrors: [],
          tariffs: [],
        };
      });

      const api = useApi().tariffs;

      try {
        this.requests.runningCount += 1;
        const [cancel, request] = api.requestTariffs(requestBody);

        this.requests.cancelFunctions.push(cancel);

        const result = await request;

        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'tariffs -> requestTariffs',
          });
        }

        this.links = result.links;
        this.calculationId = result.id;

        const unknownCompanies: number[] = [];

        // save tariffs into tariffChunk object with company id as key
        result.tarife.forEach((tariff) => {
          if (this.tariffChunks[tariff.gesellschaft.id] === undefined) {
            unknownCompanies.push(Number(tariff.gesellschaft.id));
          } else {
            this.tariffChunks[tariff.gesellschaft.id].tariffs.push(tariff);
          }
        });

        result.tariffehler.forEach((tariffError) => {
          if (this.tariffChunks[tariffError.insuranceId] === undefined) {
            unknownCompanies.push(tariffError.insuranceId);
          } else {
            this.tariffChunks[tariffError.insuranceId].tariffErrors.push(
              tariffErrorOverride(
                tariffError,
                alternateTariffSearchRequestIdentifier,
              ),
            );
          }
        });

        if (unknownCompanies.length > 0) {
          const logMsg = `tariff search included unknown companies: ${unknownCompanies.join(
            ',',
          )} - CompanyIdsChunk: ${companyIdsChunk.join(',')}`;

          logger?.warning(logMsg);
        }

        this.notes = this.notes.concat(result.hinweise);

        // add unique errors
        result.fehler.forEach((fehler) => {
          if (this.errorNotes[fehler.anbieter] === undefined) {
            this.errorNotes[fehler.anbieter] = fehler;
          } else {
            this.errorNotes[fehler.anbieter].fehler = Array.from(
              new Set([
                ...this.errorNotes[fehler.anbieter].fehler,
                ...fehler.fehler,
              ]),
            );
          }
        });

        this.allTimeTariffs = [
          ...this.allTimeTariffs,
          ...result.allTimeTariffs.map((tariff) => ({
            ...tariff,
            disableContract: true,
          })),
        ];
      } catch (error) {
        if (error instanceof BackendValidationError) {
          this.requests.lastHashCode = '';
          this.requests.cancelFunctions.forEach((cancel) => {
            cancel();
          });

          throw error;
        } else if (!(error instanceof CanceledError)) {
          throw new VglError(VGL_ERROR_CODE.TARIFFS_LOADING_CHUNK_FAILED, {
            additionalErrorMessage: `Error: ${getErrorMessage(
              error,
            )} - SearchData: ${JSON.stringify(
              searchData,
            )} - CompanyIdsChunk: ${companyIdsChunk.join(',')}`,
            cause: error,
          });
        }
      } finally {
        this.requests.runningCount -= 1;
      }
    },

    async loadTariffsByCalculationId(
      berechnungsId: string,
      logger?: UseLogger,
    ) {
      logger?.info(`loading tariffs by calculation id: ${berechnungsId}`);

      useContract().resetErrorState();

      this.tariffChunks = {};
      this.allTimeTariffs = [];
      this.errorNotes = {};
      this.notes = [];

      const api = useApi();

      try {
        this.requests.runningCount += 1;
        const [cancelTariffs, requestTariffs] =
          api.tariffs.requestTariffsByCalculationId(berechnungsId);
        const [cancelVorgaben, requestVorgaben] =
          api.vorgaben.loadVorgabenByCalculationId(berechnungsId);

        this.requests.cancelFunctions.push(cancelTariffs);
        this.requests.cancelFunctions.push(cancelVorgaben);

        // VGL-7196: Beim Deep-Link-Einsprung (z. B. aus der Agent-Tarifsuche) wurde die
        // Vorgabenseite nie besucht, die Gesellschaftsliste ist also leer. Der Direkt-
        // vereinbarungs-Filter würde damit alle Tarife ausblenden. Die Gesellschaften
        // laden wir parallel mit; scheitert nur dieser Call, bleiben die Tarife
        // ungefiltert sichtbar, statt den Einsprung mit einem Modal zu blockieren.
        const requestCompanies = useCompaniesStore()
          .loadCompanies()
          .then(() => true)
          .catch((error) => {
            logger?.warning(
              `companies failed to load, showing tariffs unfiltered: ${getErrorMessage(error)}`,
            );
            return false;
          });

        const [result, vorgabenResult, companiesLoaded] = await Promise.all([
          requestTariffs,
          requestVorgaben,
          requestCompanies,
        ]);

        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'tariffs -> requestTariffsByCalculationId',
          });
        }

        this.links = result.links;
        this.calculationId = result.id;

        const companyIds = new Set<string>();
        result.tarife.forEach((tariff) => {
          companyIds.add(tariff.gesellschaft.id);
        });
        result.tariffehler.forEach((tariffError) => {
          companyIds.add(String(tariffError.insuranceId));
        });

        // VGL-7196: Gesellschaften, die der Agent-Lauf angefragt hat, aber ohne Tarif und ohne
        // Tariffehler blieben, bekommen einen leeren Chunk. Sonst hält der Store sie für „noch nicht
        // angefragt" und der Direktvereinbarungs-Toggle würde sie erneut beim Tarifservice anfragen.
        result.angefragteGesellschaften.forEach((insuranceId) => {
          companyIds.add(String(insuranceId));
        });

        companyIds.forEach((id) => {
          this.tariffChunks[id] = {
            tariffErrors: [],
            tariffs: [],
          };
        });

        result.tarife.forEach((tariff) => {
          this.tariffChunks[tariff.gesellschaft.id].tariffs.push(tariff);
        });

        result.tariffehler.forEach((tariffError) => {
          this.tariffChunks[String(tariffError.insuranceId)].tariffErrors.push(
            tariffError,
          );
        });

        this.notes = result.hinweise;

        result.fehler.forEach((fehler) => {
          this.errorNotes[fehler.anbieter] = fehler;
        });

        this.allTimeTariffs = result.allTimeTariffs.map((tariff) => ({
          ...tariff,
          disableContract: true,
        }));

        if (vorgabenResult !== null && Object.keys(vorgabenResult).length > 0) {
          await transferVorgabenDataToStores(vorgabenResult, logger);

          if (
            vorgabenResult.konfigurationsParameter?.direktvereinbarung !==
            undefined
          ) {
            // Eine leere Liste zählt wie ein Ladefehler: Bei einem Tarifservice-Ausfall
            // antwortet das Backend mit 200 und [], der Filter würde sonst alles ausblenden
            this.filters.directAgreement =
              companiesLoaded &&
              useCompaniesStore().data.length > 0 &&
              vorgabenResult.konfigurationsParameter.direktvereinbarung;
          }
        }

        // Die Vorgaben stehen jetzt in den Stores, also entspricht die geladene Berechnung dem
        // aktuellen Such-Hash. Ohne diesen Abgleich rechnet „Zurück" aus dem Antrag (Route ohne
        // berechnungsid) alles neu und der Stepper hält den Antrag-Step für ungültig.
        this.requests.lastHashCode = useGlobalStore().getTariffSearchHash();

        logger?.info(
          `tariffs loaded successfully by calculation id: ${berechnungsId} (hash: ${this.requests.lastHashCode})`,
        );

        this.verifySelectionAndRecommendation();
      } catch (error) {
        if (error instanceof BackendValidationError) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.TARIFFS_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - BerechnungsId: ${berechnungsId}`,
          cause: error,
        });
      } finally {
        this.requests.runningCount -= 1;
        this.requests.cancelFunctions = [];
      }
    },

    addToSelection(id: string) {
      this.selection.push(id);
      if (this.selection.length > 3) {
        const removedId = this.selection.shift();

        if (this.recommendation === removedId) {
          this.setRecommendation();
        }
      }
    },

    removeFromSelection(id: string, selectionIndex?: number) {
      let index = selectionIndex;
      if (index === undefined) {
        index = this.selection.findIndex(
          (idInSelection) => idInSelection === id,
        );
      }
      if (index !== -1 && index !== undefined) {
        this.selection.splice(index, 1);
      }
    },

    setRecommendation(id?: string) {
      this.recommendation = id ?? null;
    },

    toggleSelection(tariff: BaseTariff) {
      const index = this.selection.findIndex((id) => id === tariff.identifier);

      if (index !== -1) {
        this.removeFromSelection(tariff.identifier, index);

        if (this.recommendation === tariff.identifier) {
          this.setRecommendation();
        }
      } else {
        this.addToSelection(tariff.identifier);
      }
    },

    toggleRecommendation(tariff: BaseTariff) {
      if (
        this.recommendation === null ||
        this.recommendation === '' ||
        this.recommendation !== tariff.identifier
      ) {
        this.setRecommendation(tariff.identifier);
        this.removeFromSelection(tariff.identifier);
        this.addToSelection(tariff.identifier);
      } else {
        this.setRecommendation();
      }
    },

    verifySelectionAndRecommendation() {
      if (
        this.recommendation !== null &&
        [...this.activeTariffs, ...this.allTimeTariffs].filter(
          (tariff) => tariff.identifier === this.recommendation,
        ).length === 0
      ) {
        this.setRecommendation(undefined);
      }

      if (this.selection.length > 0) {
        const flatActiveIdentifierMap: string[] = [];
        [...this.activeTariffs, ...this.allTimeTariffs].forEach((tariff) => {
          flatActiveIdentifierMap.push(tariff.identifier);
        });

        this.selection = this.selection.filter((selectedTariff) =>
          flatActiveIdentifierMap.includes(selectedTariff),
        );
      }
    },

    prepareDocuments() {
      this.documents = {};
      (this.selectedTariffs as TariffWithDocuments[]).forEach((tariff) => {
        this.documents[tariff.identifier] = tariff.dokumente.map(
          (document) => ({
            name: document,
            checked: this.isRecommendation(tariff),
          }),
        );
      });
    },
  },

  getters: {
    isLoading: (state) => state.requests.runningCount > 0,

    percentageLoaded: (state) => {
      const val =
        100 - (state.requests.runningCount / state.requests.maxCount) * 100;

      if (state.requests.runningCount > 0 && val < 25) {
        return 25;
      }

      return val;
    },

    activeCompanyIds: (state): string[] => {
      const companyIds: string[] = [];

      useCompaniesStore().filteredList.forEach((company) => {
        if (!state.filters.directAgreement || company.isDirektvereinbarung) {
          companyIds.push(company.insuranceId.toString());
        }
      });

      return companyIds;
    },

    isRecommendation(state): (tariff: BaseTariff) => boolean {
      return (tariff: BaseTariff): boolean =>
        state.recommendation === tariff.identifier;
    },

    isSelected(state): (tariff: BaseTariff) => boolean {
      return (tariff: BaseTariff): boolean =>
        state.selection.some((id) => id === tariff.identifier);
    },

    allTariffs: (state): BaseTariff[] => {
      const tariffs: BaseTariff[] = [];
      each(state.tariffChunks, (companyChunk) => {
        tariffs.push(...companyChunk.tariffs);
      });

      return tariffs;
    },

    allTariffErrors: (state): TariffError[] => {
      const tariffErrors: TariffError[] = [];

      each(state.tariffChunks, (companyChunk) => {
        if (companyChunk.tariffErrors.length === 0) return;

        tariffErrors.push(...companyChunk.tariffErrors);
      });

      return orderBy(tariffErrors, 'gesellschaftsname');
    },

    activeTariffs(): BaseTariff[] {
      if (
        !this.filters.directAgreement ||
        useGlobalStore().isWebreport ||
        useGlobalStore().isVersicherungsangabenContext
      ) {
        return this.allTariffs;
      }

      return filterActiveTariffs(this.allTariffs, this.activeCompanyIds);
    },

    sortedTariffs(state): BaseTariff[] {
      const sortingDefinition = [
        'erfuellungsgrad.isLoading',
        'risikoDemver',
        ...map(state.sorting, ({ key }) => {
          const sort = sortables[key];
          return sort.handler || sort.key;
        }),
      ];

      const orderDefinition: (boolean | 'asc' | 'desc')[] = [
        'asc',
        'desc',
        ...map(state.sorting, 'order'),
      ];

      if (!useBrokerStore().settings.data.highlightRisikoDEMVer) {
        sortingDefinition.splice(1, 1);
        orderDefinition.splice(1, 1);
      }

      return orderBy(this.activeTariffs, sortingDefinition, orderDefinition);
    },

    filteredTariffs(): { matches: BaseTariff[]; mismatches: BaseTariff[] } {
      if (this.localFilters.search.length === 0) {
        return {
          matches: this.sortedTariffs,
          mismatches: [],
        };
      }

      return filterTariffs(this.sortedTariffs, this.localFilters.search);
    },

    selectionHasRisikoDEMVer(state): boolean {
      let foundDEMVer = false;
      this.selectedTariffs.forEach((tariff) => {
        if (
          !foundDEMVer &&
          tariff.risikoDemver &&
          state.selection.includes(tariff.identifier)
        ) {
          foundDEMVer = true;
        }
      });

      return foundDEMVer;
    },

    selectedTariffsWithAllTimeTariff(): BaseTariff[] {
      const tariffs: BaseTariff[] = [];

      this.selection.forEach((selectedTariffId) => {
        const tariff = this.activeTariffs.find(
          (t) => t.identifier === selectedTariffId,
        );

        if (tariff) {
          tariffs.push(tariff);
        } else {
          const allTimeTariff = this.allTimeTariffs.find(
            (t) => t.identifier === selectedTariffId,
          );
          if (allTimeTariff) {
            tariffs.push(allTimeTariff);
          }
        }
      });

      return tariffs;
    },

    selectedTariffs(): BaseTariff[] {
      const tariffs: BaseTariff[] = [];

      this.selection.forEach((selectedTariffId) => {
        const tariff = this.activeTariffs.find(
          (t) => t.identifier === selectedTariffId,
        );

        if (tariff) {
          tariffs.push(tariff);
        }
      });

      return tariffs;
    },

    recommendedTariff(): BaseTariff | null {
      return (
        this.activeTariffs.find(
          (tariff) => tariff.identifier === this.recommendation,
        ) || null
      );
    },

    searchStringForHighlighter(state): string {
      return state.localFilters.search.join(' ');
    },

    uniqueNotes(state): TariffResponseNote[] {
      const uniqueNotes: TariffNoteMap = {};

      state.notes.forEach((note) => {
        if (uniqueNotes[note.anbieter] === undefined) {
          uniqueNotes[note.anbieter] = note;
        } else {
          note.fachlicheFehler.forEach((error) => {
            if (!uniqueNotes[note.anbieter].fachlicheFehler.includes(error)) {
              uniqueNotes[note.anbieter].fachlicheFehler.push(
                applyTranslation(error),
              );
            }
          });

          note.technischeFehler.forEach((error) => {
            if (!uniqueNotes[note.anbieter].technischeFehler.includes(error)) {
              uniqueNotes[note.anbieter].technischeFehler.push(
                applyTranslation(error),
              );
            }
          });
        }
      });

      return Object.values(uniqueNotes);
    },

    warningsToShow(): TariffResponseNote[] {
      return this.uniqueNotes.filter((note) => note.fachlicheFehler.length > 0);
    },

    errorsToShow(state): TariffResponseErrorNote[] {
      const mappedErrors = Object.entries(state.errorNotes).map(
        ([, errorNote]) => ({
          anbieter: errorNote.anbieter,
          fehler: errorNote.fehler,
        }),
      );

      return mappedErrors.filter((errorNote) => errorNote.fehler.length > 0);
    },

    contractTariff(): BaseTariff {
      const tariff = this.recommendedTariff;

      if (tariff === null || tariff === undefined) {
        throw new VglError(VGL_ERROR_CODE.TARIFFS_CONTRACT_TARIFF_NOT_FOUND);
      }

      return tariff;
    },

    hasContractTariff(): boolean {
      try {
        return (
          this.contractTariff !== null && this.contractTariff !== undefined
        );
      } catch {
        return false;
      }
    },

    byIdentifier(): Record<string, BaseTariff> {
      return reduce(
        this.activeTariffs,
        (acc: Record<string, BaseTariff>, tariff) => {
          acc[tariff.identifier] = tariff;
          return acc;
        },
        {},
      );
    },

    hasSearchInput(state): boolean {
      return state.localFilters.search.join('').length > 0;
    },

    isAllTimeTariffByIdentifier(state): (identifier: string) => boolean {
      return (identifier: string): boolean =>
        state.allTimeTariffs.some((t) => t.identifier === identifier);
    },

    getSelectedDocumentsByIdentifier(state): (identifier: string) => string[] {
      return (identifier: string): string[] =>
        state.documents[identifier]
          .filter((documents) => documents.checked)
          .map((document) => document.name);
    },

    hasDocumentsSelected(state): boolean {
      let match = false;
      Object.values(state.documents).every((documents) => {
        match = documents.some((document) => document.checked);

        return !match;
      });

      return match;
    },
  },
});
